import React, { useState, useEffect, useContext, useRef } from 'react';
import { doc, updateDoc, getDoc, setDoc, collection, addDoc, serverTimestamp, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTimes,
  faMapMarkerAlt,
  faSpinner,
  faChevronDown,
  faCalendarAlt,
  faCrown
} from '@fortawesome/free-solid-svg-icons';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { algoliasearch } from 'algoliasearch';
import UserContext from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import defaultHotelImage from '../../assets/Images/defaulthotelpicture.png';
import styles from './HotelSearchModal.module.css';

// Initialize Algolia client
const client = algoliasearch("6DJNAZSX4P", "342932cbe26f7751ec87fa2b1ff32e4c");

// Upgrade Modal Component
const UpgradeModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  const handleUpgrade = () => {
    navigate('/membership');
    onClose();
  };
  
  return (
    <div className={styles.upgradeModalOverlay} onClick={onClose}>
      <div className={styles.upgradeModal} onClick={e => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h3 className={styles.upgradeModalTitle}>
          <FontAwesomeIcon icon={faCrown} className={styles.crownIcon} />
          Upgrade to Elite
        </h3>
        <p className={styles.upgradeModalMessage}>
          Upgrade to 'Elite' membership to access advanced visibility controls and premium features.
        </p>
        <div className={styles.upgradeModalActions}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
          >
            Maybe Later
          </button>
          <button
            type="button"
            onClick={handleUpgrade}
            className={styles.upgradeButton}
          >
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
};

const HotelSearchModal = ({ isOpen, onClose, eventData, dateId, editingHotel, membershipTier, onSave, eventType = 'tour' }) => {
  const navigate = useNavigate();
  const { userData } = useContext(UserContext);
  const [hotelSearchQuery, setHotelSearchQuery] = useState('');
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [hotelSearchResults, setHotelSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isGoogleSearching, setIsGoogleSearching] = useState(false);
  const [isSearchButtonPressed, setIsSearchButtonPressed] = useState(false);
  const [isMapSearchButtonPressed, setIsMapSearchButtonPressed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [searchMode, setSearchMode] = useState('algolia'); // 'algolia', 'google', or 'noResults'
  const [sessionToken, setSessionToken] = useState('');
  const [googleSuggestions, setGoogleSuggestions] = useState([]);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Hotel data states
  const [hotelDetails, setHotelDetails] = useState({});
  const [checkInDate, setCheckInDate] = useState(editingHotel ? new Date(editingHotel.checkInDateTime) : new Date());
  const [checkOutDate, setCheckOutDate] = useState(editingHotel ? new Date(editingHotel.checkOutDateTime) : new Date());
  const [checkInTime, setCheckInTime] = useState(editingHotel ? new Date(editingHotel.checkInDateTime) : new Date());
  const [checkOutTime, setCheckOutTime] = useState(editingHotel ? new Date(editingHotel.checkOutDateTime) : new Date());
  const [selectedGroup, setSelectedGroup] = useState(membershipTier === 'Free' ? 'All' : (editingHotel ? editingHotel.group : 'Artists'));
  const [customGroupName, setCustomGroupName] = useState('');
  const [visibility, setVisibility] = useState(editingHotel?.visibility || 'All');
  const [selectedCustomUsers, setSelectedCustomUsers] = useState([]);
  const [showCustomUsersDropdown, setShowCustomUsersDropdown] = useState(false);
  const [showVisibilityDropdown, setShowVisibilityDropdown] = useState(false);

  const usersDropdownRef = useRef(null);
  const visibilityDropdownRef = useRef(null);
  const groupOptions = ['All', 'Artists', 'Crew', 'Manager', 'Custom'];
  const visibilityOptions = ['All', 'Artist', 'Crew', 'Me', 'Custom'];

  // Google API key
  const GOOGLE_API_KEY = 'AIzaSyBB7qZR3v2RlLctMh3MsApdVGoDhzLpXJM';

  // Parse address function
  const parseLocationFromAddress = (formattedAddress) => {
    if (!formattedAddress) return { city: '', state: '', country: '', pincode: '' };
    
    // Split the address by commas
    const parts = formattedAddress.split(',').map(part => part.trim());
    
    // Need at least 3 parts for this to work (city, state+pincode, country)
    if (parts.length < 3) return { city: '', state: '', country: '', pincode: '' };
    
    // Get the country from the last part
    const country = parts[parts.length - 1];
    
    // Get the state and pincode from the second-to-last part (e.g., "Maharashtra 400059")
    const stateAndPincode = parts[parts.length - 2];
    
    // Try to extract the pincode using a regex pattern
    // Looking for a sequence of digits (typically 5-6 digits for a pincode)
    const pincodeMatch = stateAndPincode.match(/\b(\d{5,6})\b/);
    
    let state = stateAndPincode;
    let pincode = '';
    
    if (pincodeMatch) {
      pincode = pincodeMatch[0].trim();
      // Remove the pincode from the state part
      state = stateAndPincode.replace(pincodeMatch[0], '').trim();
    }
    
    // Get the city from the third-to-last part
    const city = parts[parts.length - 3].trim();
    
    return { city, state, country, pincode };
  };

  // Generate a session token for Google Places API
  useEffect(() => {
    generateSessionToken();
    
    // Reset states when component unmounts
    return () => {
      setIsSearching(false);
      setIsGoogleSearching(false);
      setIsLoading(false);
    };
  }, []);

  // Reset states when modal is opened/closed
  useEffect(() => {
    if (!isOpen) {
      setIsSearching(false);
      setIsGoogleSearching(false);
      setIsLoading(false);
    } else {
      // Set initial values when editing
      if (editingHotel) {
        setHotelDetails(editingHotel.venueDetails.venueData);
        const checkInDateTime = new Date(editingHotel.checkInDateTime);
        const checkOutDateTime = new Date(editingHotel.checkOutDateTime);
        setCheckInDate(checkInDateTime);
        setCheckInTime(checkInDateTime);
        setCheckOutDate(checkOutDateTime);
        setCheckOutTime(checkOutDateTime);
        
        // Only set the group if the user is not on Free tier
        // Otherwise, keep it as 'All'
        if (membershipTier !== 'Free') {
          setSelectedGroup(editingHotel.group);
        } else {
          setSelectedGroup('All');
        }
        
        setCustomGroupName(editingHotel.customGroupName || '');
        setVisibility(editingHotel.visibility || 'All');
        setSelectedCustomUsers(editingHotel.selectedUsers || []);
      } else {
        resetForm();
      }
    }
  }, [isOpen, editingHotel, membershipTier]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (usersDropdownRef.current && !usersDropdownRef.current.contains(event.target)) {
        setShowCustomUsersDropdown(false);
      }
      if (visibilityDropdownRef.current && !visibilityDropdownRef.current.contains(event.target)) {
        setShowVisibilityDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const generateSessionToken = () => {
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    setSessionToken(uuid);
  };

  // Reset form on close
  const resetForm = () => {
    setHotelDetails({});
    setHotelSearchQuery('');
    setMapSearchQuery('');
    setHotelSearchResults([]);
    setGoogleSuggestions([]);
    setCheckInDate(new Date());
    setCheckOutDate(new Date());
    setCheckInTime(new Date());
    setCheckOutTime(new Date());
    
    // Set default group based on membership tier
    setSelectedGroup(membershipTier === 'Free' ? 'All' : 'Artists');
    
    setCustomGroupName('');
    setVisibility('All');
    setSelectedCustomUsers([]);
    setIsSearchButtonPressed(false);
    setIsMapSearchButtonPressed(false);
    setSearchMode('algolia');
  };

  // Algolia hotel search
  const handleAlgoliaSearch = async () => {
    if (!hotelSearchQuery.trim() || isSearching || isGoogleSearching) return;
    
    try {
      setIsSearching(true);
      setSearchMode('algolia');
      
      const { results } = await client.search([{
        indexName: 'places_index',
        query: hotelSearchQuery,
        hitsPerPage: 5
      }]);
  
      if (results[0].hits.length === 0) {
        setHotelSearchResults([]);
        setSearchMode('noResults');
      } else {
        setHotelSearchResults(results[0].hits);
      }
      
      // Set search button pressed to true to show results
      setIsSearchButtonPressed(true);
    } catch (error) {
      console.error('Error searching hotels:', error);
      setHotelSearchResults([]);
      setSearchMode('noResults');
      setIsSearchButtonPressed(true);
    } finally {
      setIsSearching(false);
    }
  };

  // Google Places API autocomplete search
  const handleGoogleAutocomplete = async () => {
    if (!mapSearchQuery.trim() || isGoogleSearching) return;
    
    try {
      setIsGoogleSearching(true);
      setSearchMode('google');
      
      const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_API_KEY,
        },
        body: JSON.stringify({
          input: mapSearchQuery,
          sessionToken: sessionToken,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.suggestions && data.suggestions.length > 0) {
        setGoogleSuggestions(data.suggestions);
      } else {
        setGoogleSuggestions([]);
      }
      
      setIsMapSearchButtonPressed(true);
    } catch (error) {
      console.error('Error searching with Google Places API:', error);
      setGoogleSuggestions([]);
      setIsMapSearchButtonPressed(true);
    } finally {
      setIsGoogleSearching(false);
    }
  };

  const handleHotelSearch = () => {
    // Default to Algolia search first
    handleAlgoliaSearch();
  };

  const handleClearSearch = () => {
    setHotelSearchQuery('');
    setHotelSearchResults([]);
    setIsSearchButtonPressed(false);
    setSearchMode('algolia');
  };

  const handleClearMapSearch = () => {
    setMapSearchQuery('');
    setGoogleSuggestions([]);
    setIsMapSearchButtonPressed(false);
  };

  // Handle selection of hotel from Algolia search results
  const handleSelectAlgoliaHotel = (hotel) => {
    setHotelSearchQuery(hotel.displayName);
    setHotelSearchResults([]);
    setIsSearchButtonPressed(false);
  
    // Extract location data from address if not available
    const { city, state, country, pincode } = hotel.city && hotel.state && hotel.country 
      ? { 
          city: hotel.city || '', 
          state: hotel.state || '', 
          country: hotel.country || '', 
          pincode: hotel.pincode || '' 
        }
      : parseLocationFromAddress(hotel.formattedAddress);
  
    // Update the hotel details with the selected hotel data
    setHotelDetails({
      id: hotel.objectID || '',
      name: hotel.name || '',
      displayName: hotel.displayName || '',
      formattedAddress: hotel.formattedAddress || '',
      shortFormattedAddress: hotel.shortFormattedAddress || '',
      adrFormatAddress: hotel.adrFormatAddress || '',
      location: hotel.location || null,
      plusCode: hotel.plusCode || '',
      types: hotel.types || [],
      viewport: hotel.viewport || null,
      accessibilityOptions: hotel.accessibilityOptions || {},
      businessStatus: hotel.businessStatus || '',
      googleMapsUri: hotel.googleMapsUri || '',
      iconBackgroundColor: hotel.iconBackgroundColor || '',
      iconMaskBaseUri: hotel.iconMaskBaseUri || '',
      primaryType: hotel.primaryType || '',
      primaryTypeDisplayName: hotel.primaryTypeDisplayName || '',
      subDestinations: hotel.subDestinations ? hotel.subDestinations.map(destination => destination.name).join(', ') : '',
      utcOffsetMinutes: hotel.utcOffsetMinutes || '',
      currentOpeningHours: hotel.currentOpeningHours || '',
      currentSecondaryOpeningHours: hotel.currentSecondaryOpeningHours || '',
      internationalPhoneNumber: hotel.internationalPhoneNumber || '',
      nationalPhoneNumber: hotel.nationalPhoneNumber || '',
      priceLevel: hotel.priceLevel || '',
      rating: hotel.rating || '',
      regularOpeningHours: hotel.regularOpeningHours || '',
      regularSecondaryOpeningHours: hotel.regularSecondaryOpeningHours || '',
      userRatingCount: hotel.userRatingCount || '',
      websiteUri: hotel.websiteUri || '',
      placeCoverPhotoUrl: hotel.placeCoverPhotoUrl || '',
      // Add location fields
      city,
      state,
      country,
      pincode,
    });
  };

  // Fetch photo from Google Places API
  const fetchPhotoUrl = async (placeId, photoName) => {
    try {
      const requestUrl = `https://places.googleapis.com/v1/${photoName}/media?maxWidthPx=400&maxHeightPx=400&key=${GOOGLE_API_KEY}`;
      const response = await fetch(requestUrl, {
        method: 'GET',
      });

      if (response.ok) {
        // Just return the URL directly
        return response.url;
      }
      return '';
    } catch (error) {
      console.error('Error fetching photo:', error);
      return '';
    }
  };

  // Handle selection of hotel from Google Places API results
  const handleSelectGooglePlace = async (placeId) => {
    try {
      setIsSearching(true);
      
      // Fetch place details directly from Google Places API
      const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_API_KEY,
          'X-Goog-FieldMask': 'id,name,photos,addressComponents,adrFormatAddress,formattedAddress,location,plusCode,shortFormattedAddress,types,viewport,accessibilityOptions,businessStatus,displayName,googleMapsUri,iconBackgroundColor,iconMaskBaseUri,primaryType,primaryTypeDisplayName,subDestinations,utcOffsetMinutes,currentOpeningHours,currentSecondaryOpeningHours,internationalPhoneNumber,nationalPhoneNumber,priceLevel,rating,regularOpeningHours,regularSecondaryOpeningHours,userRatingCount,websiteUri',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const placeData = await response.json();
      
      // Parse location from formattedAddress
      const { city, state, country, pincode } = parseLocationFromAddress(placeData.formattedAddress || '');
      
      // Fetch photo if available
      let placeCoverPhotoUrl = '';
      if (placeData.photos && placeData.photos.length > 0) {
        const photoName = placeData.photos[0].name;
        placeCoverPhotoUrl = await fetchPhotoUrl(placeData.id, photoName);
      }
      
      // Set hotel details with the place data
      setHotelDetails({
        id: placeData.id || '',
        name: placeData.name || '',
        displayName: placeData.displayName?.text || '',
        formattedAddress: placeData.formattedAddress || '',
        shortFormattedAddress: placeData.shortFormattedAddress || '',
        adrFormatAddress: placeData.adrFormatAddress || '',
        location: placeData.location || null,
        plusCode: placeData.plusCode?.globalCode || '',
        types: placeData.types || [],
        viewport: placeData.viewport
          ? {
              northeast: {
                latitude: placeData.viewport.high?.latitude || '',
                longitude: placeData.viewport.high?.longitude || '',
              },
              southwest: {
                latitude: placeData.viewport.low?.latitude || '',
                longitude: placeData.viewport.low?.longitude || '',
              },
            }
          : null,
        accessibilityOptions: placeData.accessibilityOptions || {},
        businessStatus: placeData.businessStatus || '',
        googleMapsUri: placeData.googleMapsUri || '',
        iconBackgroundColor: placeData.iconBackgroundColor || '',
        iconMaskBaseUri: placeData.iconMaskBaseUri || '',
        primaryType: placeData.primaryType || '',
        primaryTypeDisplayName: placeData.primaryTypeDisplayName?.text || '',
        subDestinations: placeData.subDestinations ? placeData.subDestinations.map(destination => destination.name).join(', ') : '',
        utcOffsetMinutes: placeData.utcOffsetMinutes || '',
        currentOpeningHours: placeData.currentOpeningHours?.periods
          ? placeData.currentOpeningHours.periods.map(
              (period) => `${period.openDay} ${period.openTime} - ${period.closeDay} ${period.closeTime}`
            ).join(', ')
          : '',
        currentSecondaryOpeningHours: placeData.currentSecondaryOpeningHours?.periods
          ? placeData.currentSecondaryOpeningHours.periods.map(
              (period) => `${period.openDay} ${period.openTime} - ${period.closeDay} ${period.closeTime}`
            ).join(', ')
          : '',
        internationalPhoneNumber: placeData.internationalPhoneNumber || '',
        nationalPhoneNumber: placeData.nationalPhoneNumber || '',
        priceLevel: placeData.priceLevel || '',
        rating: placeData.rating || '',
        regularOpeningHours: placeData.regularOpeningHours?.periods
          ? placeData.regularOpeningHours.periods.map(
              (period) => `${period.openDay} ${period.openTime} - ${period.closeDay} ${period.closeTime}`
            ).join(', ')
          : '',
        regularSecondaryOpeningHours: placeData.regularSecondaryOpeningHours?.periods
          ? placeData.regularSecondaryOpeningHours.periods.map(
              (period) => `${period.openDay} ${period.openTime} - ${period.closeDay} ${period.closeTime}`
            ).join(', ')
          : '',
        userRatingCount: placeData.userRatingCount || '',
        websiteUri: placeData.websiteUri || '',
        placeCoverPhotoUrl,
        // Add the parsed location fields
        city,
        state,
        country,
        pincode,
      });

      // Clear search results and queries
      setMapSearchQuery('');
      setGoogleSuggestions([]);
      setIsMapSearchButtonPressed(false);
      
      // Close the map modal
      setShowMapModal(false);
    } catch (error) {
      console.error('Error fetching place details:', error);
      alert('Failed to fetch hotel details. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // Handle custom users selection
  const handleUserSelection = (user) => {
    if (selectedCustomUsers.includes(user)) {
      setSelectedCustomUsers(selectedCustomUsers.filter(u => u !== user));
    } else {
      setSelectedCustomUsers([...selectedCustomUsers, user]);
    }
  };

  const handleVisibilityChange = (option) => {
    setVisibility(option);
    setShowVisibilityDropdown(false);
  };

  // Get all potential users for custom visibility
  const getAllUsers = () => {
    const users = [];
    
    // Add artists
    if (eventData?.artistDetails?.length) {
      eventData.artistDetails.forEach(artist => {
        if (artist.name) users.push(artist.name);
      });
    }
    
    // Add crew
    if (eventData?.crewDetails?.length) {
      eventData.crewDetails.forEach(crew => {
        if (crew.name) users.push(crew.name);
      });
    }
    
    // Add creator
    if (eventData?.createdBy?.fullName) {
      users.push(eventData.createdBy.fullName);
    }
    
    return users.filter(Boolean);
  };

  // Store selected place in Firestore
  const storePlaceInFirestore = async (placeData) => {
    try {
      const db = getFirestoreInstance();
      const placeRef = doc(db, 'Places', placeData.id);
      
      // Check if place already exists
      const placeDoc = await getDoc(placeRef);
      
      if (!placeDoc.exists()) {
        // Only store if it doesn't exist yet
        await setDoc(placeRef, placeData);
        console.log('Place stored in Firestore for Algolia syncing');
      }
    } catch (error) {
      console.error('Error storing place in Firestore:', error);
    }
  };

  // Handle save/update hotel
  const handleAddOrUpdateHotel = async () => {
    try {
      setIsLoading(true);
      setLoadingText(editingHotel ? 'Updating hotel...' : 'Adding hotel...');
  
      // Enhanced validation with better error messages
      if (!eventData) {
        console.error('Missing event data:', { eventData });
        alert('Missing event data. Please try again.');
        setIsLoading(false);
        return;
      }
      
      if (!hotelDetails || Object.keys(hotelDetails).length === 0) {
        console.error('Missing hotel details:', { hotelDetails });
        alert('Please select a hotel from the search results first.');
        setIsLoading(false);
        return;
      }
      
      if (!dateId) {
        console.error('Missing date ID:', { dateId });
        alert('Missing event date information. Please try again.');
        setIsLoading(false);
        return;
      }
  
      // Store place in Firestore for Algolia syncing
      await storePlaceInFirestore(hotelDetails);
  
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
  
      // Create check-in and check-out datetime objects
      const checkInDateTime = new Date(
        checkInDate.getFullYear(),
        checkInDate.getMonth(),
        checkInDate.getDate(),
        checkInTime.getHours(),
        checkInTime.getMinutes()
      );
  
      const checkOutDateTime = new Date(
        checkOutDate.getFullYear(),
        checkOutDate.getMonth(),
        checkOutDate.getDate(),
        checkOutTime.getHours(),
        checkOutTime.getMinutes()
      );
  
      // Create hotel data object
      const hotelData = {
        venueDetails: { venueData: hotelDetails },
        checkInDateTime: checkInDateTime.toISOString(),
        checkOutDateTime: checkOutDateTime.toISOString(),
        group: selectedGroup,
        customGroupName: selectedGroup === 'Custom' ? customGroupName : '',
        rooms: editingHotel ? editingHotel.rooms : 0,
        roomDetails: editingHotel ? editingHotel.roomDetails || [] : [],
        visibility,
        selectedUsers: visibility === 'Custom' ? selectedCustomUsers : [],
        creatorId: editingHotel ? editingHotel.creatorId : userData.userId,
        lastUpdated: new Date().toISOString(),
        createdAt: editingHotel ? editingHotel.createdAt : serverTimestamp(),
        updatedAt: serverTimestamp()
      };
  
      // Check for existing hotels with the same venue ID and group
      if (!editingHotel) {
        // When adding a new hotel, check for duplicates in the hotels subcollection
        const hotelsQuery = collection(db, collectionName, eventData.id, 'eventDates', dateId, 'hotels');
        const hotelsSnapshot = await getDocs(hotelsQuery);
        
        const duplicateHotel = hotelsSnapshot.docs.find(doc => {
          const hotel = doc.data();
          return hotel.venueDetails?.venueData?.id === hotelDetails.id && hotel.group === selectedGroup;
        });
        
        if (duplicateHotel) {
          alert('This hotel is already added for this group.');
          setIsLoading(false);
          return;
        }
      }
  
      // Save the hotel to Firestore
      if (editingHotel) {
        // Update existing hotel document
        const hotelRef = doc(db, collectionName, eventData.id, 'eventDates', dateId, 'hotels', editingHotel.id);
        await updateDoc(hotelRef, hotelData);
      } else {
        // Add new hotel document to the hotels subcollection
        await addDoc(collection(db, collectionName, eventData.id, 'eventDates', dateId, 'hotels'), hotelData);
      }
  
      // Update the event date to trigger the listener
      const eventDateRef = doc(db, collectionName, eventData.id, 'eventDates', dateId);
      await updateDoc(eventDateRef, {
        dateLastUpdated: serverTimestamp(),
        // Ensure hotel data is reflected in the event date document as well
        hotel: true,  // Just adding a flag to indicate hotel data is present
        updatedAt: serverTimestamp()
      });
  
      // Call the onSave callback to update the parent state
      if (onSave) {
        console.log('Calling onSave callback to update parent state');
        await onSave();
      }
  
      setIsLoading(false);
      onClose();
      alert(editingHotel ? 'Hotel updated successfully' : 'Hotel added successfully');
  
    } catch (error) {
      console.error('Error adding/updating hotel:', error);
      setIsLoading(false);
      alert('Failed to save hotel. Please try again.');
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && !isSearching && !isGoogleSearching) {
      handleHotelSearch();
    }
  };

  const handleMapSearchKeyDown = (e) => {
    if (e.key === 'Enter' && !isGoogleSearching && !isSearching) {
      handleGoogleAutocomplete();
    }
  };

  // Show upgrade modal
  const handleShowUpgradeModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowUpgradeModal(true);
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  // Map Search Modal
  const MapSearchModal = React.memo(({ 
    mapSearchQuery, 
    setMapSearchQuery, 
    isGoogleSearching,
    handleGoogleAutocomplete,
    googleSuggestions,
    isMapSearchButtonPressed,
    handleClearMapSearch,
    handleMapSearchKeyDown,
    handleSelectGooglePlace,
    setShowMapModal
  }) => (
    <div className={styles.mapModalOverlay} onClick={() => setShowMapModal(false)}>
      <div className={styles.mapModalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.mapModalHeader}>
          <h3>Search Hotel on Map</h3>
          <button onClick={() => setShowMapModal(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className={styles.mapSearchContainer}>
  <div className={styles.searchInputContainer}>
    <div className={styles.mapSearchInputWrapper}>
      <input
        type="text"
        placeholder="Search hotel"
        className={styles.mapSearchInput}
        value={mapSearchQuery}
        onChange={e => setMapSearchQuery(e.target.value)}
        onKeyDown={handleMapSearchKeyDown}
        autoFocus
      />
      {mapSearchQuery !== '' && (
        <button 
          type="button"
          className={styles.clearButton}
          onClick={handleClearMapSearch}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
    <button 
      type="button"
      className={styles.searchButton}
      onClick={handleGoogleAutocomplete}
      disabled={!mapSearchQuery.trim() || isGoogleSearching}
    >
      {isGoogleSearching ? (
        <FontAwesomeIcon icon={faSpinner} spin />
      ) : (
        <FontAwesomeIcon icon={faSearch} />
      )}
      <span>Search</span>
    </button>
  </div>
  
  {isGoogleSearching ? (
    <div className={styles.loadingContainer}>
      <FontAwesomeIcon icon={faSpinner} spin size="2x" />
      <p>Searching...</p>
    </div>
  ) : isMapSearchButtonPressed && googleSuggestions.length > 0 ? (
    <div className={styles.googleResults}>
      {googleSuggestions.map((suggestion) => (
        <div
          key={suggestion.placePrediction.placeId}
          className={styles.googleResult}
          onClick={() => handleSelectGooglePlace(suggestion.placePrediction.placeId)}
        >
          <div className={styles.googleResultText}>
            {suggestion.placePrediction.text.text}
          </div>
        </div>
      ))}
    </div>
  ) : isMapSearchButtonPressed && googleSuggestions.length === 0 ? (
    <div className={styles.emptySearchResults}>
      <p>No hotels found. Try a different search term.</p>
    </div>
  ) : (
    <div className={styles.emptySearchResults}>
      <p>Enter search terms and click Search to find hotels.</p>
    </div>
  )}
</div>
        
        <div className={styles.mapModalActions}>
          <button onClick={() => setShowMapModal(false)}>Cancel</button>
        </div>
      </div>
    </div>
  ));

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{editingHotel ? 'Edit Hotel' : 'Add Hotel'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.searchContainer}>
  <div className={styles.searchInputWrapper}>
    <input
      type="text"
      className={styles.searchInput}
      placeholder="Search hotel in our database"
      value={hotelSearchQuery}
      onChange={e => setHotelSearchQuery(e.target.value)}
      onKeyDown={handleSearchKeyDown}
    />
    {hotelSearchQuery !== '' && (
      <button className={styles.clearButton} onClick={handleClearSearch}>
        <FontAwesomeIcon icon={faTimes} />
      </button>
    )}
    
    {/* Show search results dropdown conditionally */}
    {isSearchButtonPressed && !isSearching && searchMode === 'algolia' && hotelSearchResults.length > 0 && (
      <div className={styles.suggestionsContainer}>
        {hotelSearchResults.map((hotel) => (
          <div 
            key={hotel.objectID} 
            className={styles.suggestionItem}
            onClick={() => handleSelectAlgoliaHotel(hotel)}
          >
            <div className={styles.hotelSearchName}>{hotel.displayName}</div>
            <div className={styles.hotelSearchAddress}>{hotel.formattedAddress}</div>
          </div>
        ))}
      </div>
    )}
  </div>
  <button 
    className={`${styles.searchButton} ${hotelSearchQuery.trim() === '' ? styles.disabledButton : ''}`}
    onClick={handleHotelSearch}
    disabled={hotelSearchQuery.trim() === '' || isSearching || isGoogleSearching}
  >
    {isSearching ? (
      <FontAwesomeIcon icon={faSpinner} spin />
    ) : (
      <FontAwesomeIcon icon={faSearch} />
    )}
    <span>Search</span>
  </button>
</div>

        
        {/* No results message */}
        {isSearchButtonPressed && !isSearching && searchMode === 'noResults' && (
          <div className={styles.noResultsContainer}>
            <p className={styles.noResultsMessage}>Couldn't find hotel in our database.</p>
            <button 
              className={styles.searchMapButton}
              onClick={() => {
                setSearchMode('google');
                setShowMapModal(true);
                // Reset map search state
                setMapSearchQuery('');
                setGoogleSuggestions([]);
                setIsMapSearchButtonPressed(false);
              }}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} className={styles.buttonIcon} />
              Search Map
            </button>
          </div>
        )}

        {/* Selected hotel display */}
        {Object.keys(hotelDetails).length > 0 && (
          <div className={styles.selectedPlace}>
            <h3 className={styles.selectedLocationText}>Selected Hotel</h3>
            <div className={styles.venueDetailsContainer}>
              <div className={styles.venueImageContainer}>
                <img
                  src={hotelDetails.placeCoverPhotoUrl || defaultHotelImage}
                  alt={hotelDetails.displayName}
                  className={styles.venueImage}
                  onError={(e) => {
                    e.target.src = defaultHotelImage;
                  }}
                />
              </div>
              <div className={styles.venueTextContainer}>
                <h4 className={styles.venueDisplayName}>{hotelDetails.displayName}</h4>
                <p className={styles.venueAddress}>{hotelDetails.formattedAddress}</p>
                {(hotelDetails.city || hotelDetails.state || hotelDetails.country) && (
                  <p className={styles.venueLocation}>
                    {[
                      hotelDetails.city,
                      hotelDetails.state,
                      hotelDetails.pincode,
                      hotelDetails.country
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Form fields */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Check-In Date</label>
            <div className={styles.datePickerWrapper}>
              <DatePicker
                selected={checkInDate}
                onChange={date => setCheckInDate(date)}
                className={styles.datePicker}
                dateFormat="MMMM d, yyyy"
              />
              <FontAwesomeIcon icon={faCalendarAlt} className={styles.datePickerIcon} />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Check-In Time</label>
            <div className={styles.datePickerWrapper}>
              <DatePicker
                selected={checkInTime}
                onChange={time => setCheckInTime(time)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="h:mm aa"
                className={styles.datePicker}
              />
              <FontAwesomeIcon icon={faCalendarAlt} className={styles.datePickerIcon} />
            </div>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Check-Out Date</label>
            <div className={styles.datePickerWrapper}>
              <DatePicker
                selected={checkOutDate}
                onChange={date => setCheckOutDate(date)}
                className={styles.datePicker}
                dateFormat="MMMM d, yyyy"
              />
              <FontAwesomeIcon icon={faCalendarAlt} className={styles.datePickerIcon} />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>Check-Out Time</label>
            <div className={styles.datePickerWrapper}>
              <DatePicker
                selected={checkOutTime}
                onChange={time => setCheckOutTime(time)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="h:mm aa"
                className={styles.datePicker}
              />
              <FontAwesomeIcon icon={faCalendarAlt} className={styles.datePickerIcon} />
            </div>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>Group</label>
            {membershipTier === 'Free' ? (
              <div className={styles.visibilityInfo}>
                <span>All members</span>
                <button
                  className={styles.upgradeButton}
                  onClick={handleShowUpgradeModal}
                >
                  <FontAwesomeIcon icon={faCrown} className={styles.crownIcon} />
                  Upgrade for more options
                </button>
              </div>
            ) : (
              <div className={styles.dropdownContainer}>
                <div 
                  className={styles.dropdown}
                  onClick={() => document.getElementById('groupDropdown').classList.toggle(styles.show)}
                >
                  <span>{selectedGroup}</span>
                  <FontAwesomeIcon icon={faChevronDown} className={styles.dropdownIcon} />
                </div>
                <div id="groupDropdown" className={styles.dropdownMenu}>
                  {groupOptions.map(option => (
                    <div 
                      key={option} 
                      className={styles.dropdownItem}
                      onClick={() => {
                        setSelectedGroup(option);
                        document.getElementById('groupDropdown').classList.remove(styles.show);
                      }}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedGroup === 'Custom' && membershipTier !== 'Free' && (
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Group Name</label>
              <input
                type="text"
                className={styles.textInput}
                value={customGroupName}
                onChange={e => setCustomGroupName(e.target.value)}
                placeholder="Enter group name"
              />
            </div>
          </div>
        )}

        {membershipTier === 'Elite' && (
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Visibility</label>
              <div className={styles.dropdownContainer} ref={visibilityDropdownRef}>
                <div 
                  className={styles.dropdown}
                  onClick={() => setShowVisibilityDropdown(!showVisibilityDropdown)}
                >
                  <span>{visibility}</span>
                  <FontAwesomeIcon icon={faChevronDown} className={styles.dropdownIcon} />
                </div>
                {showVisibilityDropdown && (
                  <div className={styles.dropdownMenu}>
                    {visibilityOptions.map(option => (
                      <div 
                        key={option} 
                        className={styles.dropdownItem}
                        onClick={() => handleVisibilityChange(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {membershipTier === 'Elite' && visibility === 'Custom' && (
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Select Users</label>
              <div className={styles.customUsersContainer} ref={usersDropdownRef}>
                <div 
                  className={styles.dropdown}
                  onClick={() => setShowCustomUsersDropdown(!showCustomUsersDropdown)}
                >
                  <span>
                    {selectedCustomUsers.length > 0 
                      ? `${selectedCustomUsers.length} users selected` 
                      : 'Select users'}
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} className={styles.dropdownIcon} />
                </div>
                {showCustomUsersDropdown && (
                  <div className={styles.multiSelectDropdown}>
                    {getAllUsers().map(user => (
                      <div key={user} className={styles.checkboxItem}>
                        <input
                          type="checkbox"
                          id={`user-${user}`}
                          checked={selectedCustomUsers.includes(user)}
                          onChange={() => handleUserSelection(user)}
                        />
                        <label htmlFor={`user-${user}`}>{user}</label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className={styles.formActions}>
          <button 
            className={styles.cancelButton} 
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            className={styles.saveButton} 
            onClick={handleAddOrUpdateHotel}
            disabled={isLoading || Object.keys(hotelDetails).length === 0}
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> 
                {editingHotel ? 'Updating...' : 'Adding...'}
              </>
            ) : (
              editingHotel ? 'Update' : 'Add'
            )}
          </button>
        </div>

        {/* Loading overlay */}
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingContent}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>{loadingText || 'Loading...'}</p>
            </div>
          </div>
        )}

        {/* Map search modal */}
        {showMapModal && (
  <MapSearchModal
    mapSearchQuery={mapSearchQuery}
    setMapSearchQuery={setMapSearchQuery}
    isGoogleSearching={isGoogleSearching}
    handleGoogleAutocomplete={handleGoogleAutocomplete}
    googleSuggestions={googleSuggestions}
    isMapSearchButtonPressed={isMapSearchButtonPressed}
    handleClearMapSearch={handleClearMapSearch}
    handleMapSearchKeyDown={handleMapSearchKeyDown}
    handleSelectGooglePlace={handleSelectGooglePlace}
    setShowMapModal={setShowMapModal}
  />
)}

        {/* Upgrade modal as separate component */}
        <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      </div>
    </div>
  );
};

export default HotelSearchModal;